import { McpEntityCard } from "@/components/mcp/McpEntityCard";
import { usePluginQueryScope } from "@/pages/plugins/usePluginQueryScope";
import { useOrganization } from "@/contexts/Auth";
import { usePluginWriteAccess } from "@/hooks/usePluginWriteAccess";
import { type FilterValue, useFilterState } from "@/components/filters";
import { InputField } from "@/components/moon/input-field";
import { Page } from "@/components/page-layout";
import { Icon } from "@/components/ui/Icon";
import { ResourceListPage } from "@/components/page-templates";
import { Dialog } from "@/components/ui/Dialog";
import { Text } from "@/components/ui/Text";
import { useFetcher } from "@/contexts/Fetcher";
import { openSafeExternalUrl } from "@/lib/safe-external-url";
import { useRoutes } from "@/routes";
import type { PublishStatusResult } from "@gram/client/models/components/publishstatusresult.js";
import { Plugin } from "@gram/client/models/components/plugin.js";
import { useCreatePluginMutation } from "@gram/client/react-query/createPlugin";
import {
  invalidateAllPlugins,
  usePluginsSuspense,
} from "@gram/client/react-query/plugins";
import {
  invalidateAllPublishStatus,
  usePublishStatusSuspense,
} from "@gram/client/react-query/publishStatus";
import { usePublishPluginsMutation } from "@gram/client/react-query/publishPlugins";
import {
  invalidateAllMarketplaceSettings,
  useMarketplaceSettingsSuspense,
} from "@gram/client/react-query/marketplaceSettings";
import { useUpdateMarketplaceSettingsMutation } from "@gram/client/react-query/updateMarketplaceSettings";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { MoreActions } from "@/components/ui/MoreActions";
import { Stack } from "@/components/ui/Stack";
import { Label } from "@/components/ui/Label";
import { Switch } from "@/components/ui/Switch";
import { useRBAC } from "@/hooks/useRBAC";
import { Activity, Network } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import { toast } from "sonner";
import { PlatformInstrumentationSheet } from "../setup/components/platform-instrumentation-sheet";
import { PlatformMCPOnboardingContent } from "../org/PlatformMCP";
import {
  MarketplaceCard,
  UninitializedMarketplaceCard,
} from "./MarketplaceCard";
import { PluginCard } from "./PluginCard";
import { PluginInstallButton } from "./PluginInstallButton";
import type { ObservabilityDownloadPlatform } from "./observability-platforms";
import { OBSERVABILITY_DOWNLOAD_PLATFORMS } from "./observability-platforms";
import { RotateObservabilityCredentialDialog } from "./RotateObservabilityCredentialDialog";
import { downloadResponse } from "./downloadPluginPackage";
import {
  matchesPluginFilters,
  PLUGINS_FILTERS,
  pluginServerFilterOptions,
} from "./plugins-filter-schema";
import { PublishDialog } from "./PublishDialog";

export function PluginsRoot(): JSX.Element {
  return <Outlet />;
}

export default function Plugins(): JSX.Element | null {
  const { hasScope, isLoading } = useRBAC();
  const organization = useOrganization();
  const canWritePlugin = usePluginWriteAccess();
  if (isLoading) return null;
  if (!canWritePlugin && !hasScope("org:read", organization.id)) return null;
  return hasScope("org:admin", organization.id) ? (
    <PluginsWithMarketplaceSettings />
  ) : (
    <PluginsContent />
  );
}

function PluginsWithMarketplaceSettings(): JSX.Element {
  const scope = usePluginQueryScope();
  const { data } = useMarketplaceSettingsSuspense(scope);
  return <PluginsContent marketplaceSettings={data} />;
}

function PluginsContent({
  marketplaceSettings = {
    defaultName: "",
    effectiveName: "",
    observabilityEnabled: false,
  },
}: {
  marketplaceSettings?: ReturnType<
    typeof useMarketplaceSettingsSuspense
  >["data"];
}): JSX.Element {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createAutoSyncEnabled, setCreateAutoSyncEnabled] = useState(true);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [isManageCollaboratorsOpen, setIsManageCollaboratorsOpen] =
    useState(false);
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const routes = useRoutes();
  const navigate = useNavigate();

  const scope = usePluginQueryScope();
  const { data } = usePluginsSuspense(scope);
  // Polled so the marketplace card and per-plugin sync badges pick up the
  // Temporal generator-rollout schedule's auto-sync without a manual refresh.
  const { data: publishStatus } = usePublishStatusSuspense(scope, undefined, {
    refetchInterval: 5_000,
  });
  const { hasScope } = useRBAC();
  const canWritePlugin = usePluginWriteAccess();
  const organization = useOrganization();
  const canManageMarketplace = hasScope("org:admin", organization.id);
  const { fetch: authFetch } = useFetcher();
  const [isObservabilityDownloadMenuOpen, setIsObservabilityDownloadMenuOpen] =
    useState(false);
  const [isDownloadingObservability, setIsDownloadingObservability] =
    useState<ObservabilityDownloadPlatform | null>(null);

  const handleObservabilityDownload = async (
    platform: ObservabilityDownloadPlatform,
  ) => {
    setIsObservabilityDownloadMenuOpen(false);
    setIsDownloadingObservability(platform);
    try {
      const resp = await authFetch(
        `/rpc/plugins.downloadObservabilityPlugin?platform=${platform}`,
        {},
      );
      if (!resp.ok) {
        toast.error("Failed to download observability plugin");
        return;
      }
      await downloadResponse(resp, `observability-${platform}.zip`);
    } catch (err) {
      toast.error("Failed to download observability plugin");
      console.error("observability plugin download failed", err);
    } finally {
      setIsDownloadingObservability(null);
    }
  };

  // Ref, not state: onSuccess/onError read the mode from a detached mutation
  // callback, where a state capture would be stale; it also avoids re-rendering
  // the memoized dialogs on mode switch.
  const publishModeRef = useRef<"publish" | "manage">("publish");

  const publishMutation = usePublishPluginsMutation({
    onSuccess: (data) => {
      const managing = publishModeRef.current === "manage";
      setIsPublishDialogOpen(false);
      setIsManageCollaboratorsOpen(false);
      void invalidateAllPublishStatus(queryClient);
      toast.success(
        managing ? "Collaborators added" : "Plugins published to GitHub",
        {
          description: data.repoUrl,
          action: {
            label: "Open",
            onClick: () => {
              openSafeExternalUrl(data.repoUrl);
            },
          },
        },
      );
    },
    onError: () => {
      toast.error(
        publishModeRef.current === "manage"
          ? "Failed to add collaborators"
          : "Failed to publish plugins to GitHub",
      );
    },
  });

  const hasPlugins = (data?.plugins ?? []).length > 0;

  const pluginFilters = useFilterState(PLUGINS_FILTERS);
  const pluginFilterOptions = useMemo(
    () => pluginServerFilterOptions(data?.plugins ?? []),
    [data?.plugins],
  );

  const filteredPlugins = useMemo(() => {
    const plugins = data?.plugins ?? [];
    const q = search.trim().toLowerCase();
    return plugins.filter((p) => {
      if (!matchesPluginFilters(p, pluginFilters.values)) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q)
      );
    });
  }, [data?.plugins, search, pluginFilters.values]);

  const createMutation = useCreatePluginMutation({
    onSuccess: async (data) => {
      setIsCreateDialogOpen(false);
      await invalidateAllPlugins(queryClient);
      void navigate(routes.plugins.detail.href(data.id));
    },
  });

  const handleCreate: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!canWritePlugin) return;
    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;
    const description = formData.get("description") as string;

    createMutation.mutate({
      security: { sessionHeaderGramSession: "" },
      request: {
        createPluginForm: {
          name,
          description: description || undefined,
          autoSyncEnabled: createAutoSyncEnabled,
        },
      },
    });
  };

  const openCreateDialog = () => {
    setCreateAutoSyncEnabled(true);
    setIsCreateDialogOpen(true);
  };

  // Plugins whose auto sync is off only publish their changes on a manual
  // sync, so the marketplace card says so.
  const pausedPluginNames = useMemo(
    () =>
      (data?.plugins ?? [])
        .filter((plugin) => plugin.autoSyncEnabled === false)
        .map((plugin) => plugin.name),
    [data?.plugins],
  );

  // Destructure mutate so the dep array references the stable function
  // directly (TanStack Query keeps mutate referentially stable, but the
  // wrapper object is fresh per render). Keeps memo() on PublishDialog
  // effective and satisfies react-hooks/exhaustive-deps.
  const { mutate: publishMutate } = publishMutation;
  const handlePublish = useCallback(
    (githubUsernames: string[]) => {
      if (!canWritePlugin) return;
      publishModeRef.current = "publish";
      publishMutate({
        security: { sessionHeaderGramSession: "" },
        request: {
          publishPluginsRequestBody: { githubUsernames },
        },
      });
    },
    [canWritePlugin, publishMutate],
  );
  const handleAddCollaborators = useCallback(
    (githubUsernames: string[]) => {
      if (!canManageMarketplace) return;
      publishModeRef.current = "manage";
      publishMutate({
        security: { sessionHeaderGramSession: "" },
        request: {
          publishPluginsRequestBody: { githubUsernames },
        },
      });
    },
    [canManageMarketplace, publishMutate],
  );

  const [marketplaceNameInput, setMarketplaceNameInput] = useState(
    marketplaceSettings.marketplaceName ?? marketplaceSettings.defaultName,
  );
  const updateMarketplaceSettingsMutation =
    useUpdateMarketplaceSettingsMutation({
      onSuccess: async () => {
        await Promise.all([
          invalidateAllMarketplaceSettings(queryClient),
          invalidateAllPublishStatus(queryClient),
        ]);
      },
      onError: () => {
        toast.error("Failed to update marketplace settings");
      },
    });

  const [isMarketplaceSettingsDialogOpen, setIsMarketplaceSettingsDialogOpen] =
    useState(false);
  // Set when the settings dialog was opened from the uninitialized card's
  // "Setup" CTA rather than "Rename" on an already-connected marketplace —
  // saving the name should then chain straight into the publish dialog so
  // Setup reads as one flow (name, then collaborators) instead of two.
  const [chainToPublishAfterSave, setChainToPublishAfterSave] = useState(false);

  const trimmedMarketplaceName = marketplaceNameInput.trim();
  const currentMarketplaceName = marketplaceSettings.marketplaceName ?? "";
  const marketplaceNameDirty =
    trimmedMarketplaceName !== currentMarketplaceName.trim();

  const handleOpenMarketplaceSettings = () => {
    if (!canManageMarketplace) return;
    // Reset the input to the persisted value so reopening discards unsaved
    // edits. Falls back to the computed default name (not "") when unset —
    // otherwise the field shows only ghost placeholder text with nothing to
    // actually save, which is a dead end once naming is required (Setup).
    setMarketplaceNameInput(
      marketplaceSettings.marketplaceName ?? marketplaceSettings.defaultName,
    );
    setIsMarketplaceSettingsDialogOpen(true);
  };

  const handleStartSetup = () => {
    setChainToPublishAfterSave(true);
    handleOpenMarketplaceSettings();
  };

  const handleSaveMarketplaceName = () => {
    if (!canManageMarketplace) return;
    updateMarketplaceSettingsMutation.mutate(
      {
        security: { sessionHeaderGramSession: "" },
        request: {
          updateMarketplaceSettingsRequestBody: {
            marketplaceName: trimmedMarketplaceName,
          },
        },
      },
      {
        onSuccess: (data) => {
          setMarketplaceNameInput(data.settings.marketplaceName ?? "");
          if (data.hooksUpdateDeferred) {
            toast.warning(
              "Marketplace name updated, but the observability hooks plugin can't be updated yet: your organization isn't approved for the latest hooks version. It will update automatically once your org is rolled forward.",
            );
          } else {
            toast.success(
              data.republished
                ? "Marketplace name updated and republished"
                : "Marketplace name saved",
            );
          }
          setIsMarketplaceSettingsDialogOpen(false);
          if (chainToPublishAfterSave) {
            setChainToPublishAfterSave(false);
            setIsPublishDialogOpen(true);
          }
        },
      },
    );
  };

  const handleObservabilityEnabledChange = (enabled: boolean) => {
    updateMarketplaceSettingsMutation.mutate(
      {
        security: { sessionHeaderGramSession: "" },
        request: {
          updateMarketplaceSettingsRequestBody: {
            observabilityEnabled: enabled,
          },
        },
      },
      {
        onSuccess: (data) => {
          if (data.republished) {
            toast.success(
              enabled
                ? "Observability plugin enabled and republished"
                : "Observability plugin disabled and removed from the marketplace",
            );
            return;
          }
          toast.success(
            enabled
              ? "Observability plugin enabled"
              : "Observability plugin disabled",
          );
        },
      },
    );
  };

  return (
    <>
      <ResourceListPage
        title="Plugins"
        bodyClassName="-mt-6"
        description={
          <span className={hasPlugins ? "block w-3/4" : undefined}>
            Create distributable plugin bundles that package MCP servers and
            skills together. Assign plugins to roles and publish them to
            supported agent marketplaces via GitHub.
          </span>
        }
        primaryAction={
          canWritePlugin ? (
            <Button onClick={openCreateDialog}>
              <Button.LeftIcon>
                <Icon name="plus" />
              </Button.LeftIcon>
              <Button.Text>Create plugin</Button.Text>
            </Button>
          ) : undefined
        }
      >
        <Stack direction="vertical" gap={8}>
          {canManageMarketplace &&
            publishStatus?.configured &&
            (publishStatus.connected && publishStatus.repoUrl ? (
              publishStatus.hasCollaborators === false ? (
                <>
                  <UninitializedMarketplaceCard
                    publishStatus={publishStatus}
                    defaultName={
                      marketplaceSettings.marketplaceName ??
                      marketplaceSettings.defaultName
                    }
                    onSetup={
                      canManageMarketplace ? handleStartSetup : undefined
                    }
                    onAddCollaborators={
                      canManageMarketplace
                        ? () => setIsManageCollaboratorsOpen(true)
                        : undefined
                    }
                    observabilityEnabled={
                      marketplaceSettings.observabilityEnabled
                    }
                  />
                </>
              ) : (
                <>
                  <MarketplaceCard
                    publishStatus={publishStatus}
                    onManageCollaborators={
                      canManageMarketplace
                        ? () => setIsManageCollaboratorsOpen(true)
                        : undefined
                    }
                    onRename={
                      canManageMarketplace
                        ? handleOpenMarketplaceSettings
                        : undefined
                    }
                    onSync={
                      canWritePlugin ? () => handlePublish([]) : undefined
                    }
                    isSyncing={publishMutation.isPending}
                    observabilityEnabled={
                      marketplaceSettings.observabilityEnabled
                    }
                    pausedPluginNames={pausedPluginNames}
                  />
                </>
              )
            ) : (
              <>
                <UninitializedMarketplaceCard
                  publishStatus={publishStatus}
                  defaultName={
                    marketplaceSettings.marketplaceName ??
                    marketplaceSettings.defaultName
                  }
                  onSetup={canManageMarketplace ? handleStartSetup : undefined}
                  onAddCollaborators={
                    canManageMarketplace
                      ? () => setIsManageCollaboratorsOpen(true)
                      : undefined
                  }
                  observabilityEnabled={
                    marketplaceSettings.observabilityEnabled
                  }
                />
              </>
            ))}
          {hasPlugins && (
            <Page.Toolbar>
              <Page.Toolbar.Search
                value={search}
                onChange={setSearch}
                placeholder="Search plugins"
              />
              <Page.Toolbar.Filters
                schema={PLUGINS_FILTERS}
                values={pluginFilters.values}
                optionsById={pluginFilterOptions}
                onChange={
                  pluginFilters.setValue as (
                    id: string,
                    value: FilterValue,
                  ) => void
                }
                onClear={pluginFilters.clearValue as (id: string) => void}
                onClearAll={pluginFilters.clearAll}
              />
            </Page.Toolbar>
          )}
          <Text small muted>
            The default plugin is where all newly created MCP servers will be
            automatically published to. If you have the default plugin installed
            in your coding agent, then any new MCP servers will become instantly
            available for installation.
          </Text>
          {!canManageMarketplace &&
            canWritePlugin &&
            publishStatus?.connected && (
              <Button
                onClick={() => handlePublish([])}
                disabled={publishMutation.isPending}
              >
                Sync plugins
              </Button>
            )}
          <PluginGrid
            plugins={filteredPlugins}
            publishStatus={canManageMarketplace ? publishStatus : undefined}
            searchQuery={hasPlugins ? search : ""}
          />
          {canManageMarketplace && (
            <>
              <div className="flex items-center gap-3">
                <div className="border-border flex-1 border-t" />
                <Text
                  small
                  muted
                  className="shrink-0 font-mono text-xs tracking-wide uppercase"
                >
                  Platform Plugins
                </Text>
                <div className="border-border flex-1 border-t" />
              </div>
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <ObservabilityPluginCard
                  publishStatus={publishStatus}
                  enabled={marketplaceSettings.observabilityEnabled}
                  canToggle={canManageMarketplace}
                  isToggling={updateMarketplaceSettingsMutation.isPending}
                  onEnabledChange={handleObservabilityEnabledChange}
                  isDownloadMenuOpen={isObservabilityDownloadMenuOpen}
                  onDownloadMenuOpenChange={setIsObservabilityDownloadMenuOpen}
                  isDownloading={isDownloadingObservability !== null}
                  onDownload={(platform) => {
                    void handleObservabilityDownload(platform);
                  }}
                />
                <PlatformMCPPluginCard />
              </div>
            </>
          )}
        </Stack>
      </ResourceListPage>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Create Plugin</Dialog.Title>
            <Dialog.Description>
              Create a new plugin bundle for distributing MCP servers.
            </Dialog.Description>
          </Dialog.Header>
          <form onSubmit={handleCreate} className="flex flex-col gap-4">
            <InputField label="Name" name="name" required autoFocus />
            <InputField label="Description" name="description" />
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label id="create-plugin-auto-sync-label">
                  Sync changes automatically
                </Label>
                <Text small muted>
                  When off, the plugin reaches your marketplace only when
                  someone clicks Sync changes.
                </Text>
              </div>
              <Switch
                aria-labelledby="create-plugin-auto-sync-label"
                checked={createAutoSyncEnabled}
                onCheckedChange={setCreateAutoSyncEnabled}
              />
            </div>
            <Dialog.Footer>
              <Button
                variant="secondary"
                onClick={() => setIsCreateDialogOpen(false)}
                type="button"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                Create
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Content>
      </Dialog>

      <PublishDialog
        open={isPublishDialogOpen}
        onOpenChange={setIsPublishDialogOpen}
        onPublish={handlePublish}
        isPending={publishMutation.isPending}
      />
      <PublishDialog
        mode="manage"
        open={isManageCollaboratorsOpen}
        onOpenChange={setIsManageCollaboratorsOpen}
        onPublish={handleAddCollaborators}
        isPending={publishMutation.isPending}
      />

      {/* Marketplace Settings Dialog */}
      <Dialog
        open={isMarketplaceSettingsDialogOpen}
        onOpenChange={setIsMarketplaceSettingsDialogOpen}
      >
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Marketplace settings</Dialog.Title>
            <Dialog.Description>
              The marketplace name is the identifier your team types after the
              plugin slug ({"<plugin>@<marketplace>"}) when installing from a
              supported agent marketplace. Applies to all plugins in this
              project.
            </Dialog.Description>
          </Dialog.Header>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveMarketplaceName();
            }}
          >
            <InputField
              label="Marketplace name"
              name="marketplace_name"
              value={marketplaceNameInput}
              onChange={(e) => setMarketplaceNameInput(e.target.value)}
              placeholder={marketplaceSettings.defaultName}
              pattern="^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$"
              title="Lowercase letters, digits, and hyphens. May not start or end with a hyphen."
              // Renaming an already-published marketplace can fall back to
              // the default name, so it's genuinely optional there — but
              // mid-Setup this is the one deliberate naming step, so it
              // reads as required (also hides the "optional" label via
              // AnyField's group-has-[[required]] rule).
              required={chainToPublishAfterSave}
              autoFocus
            />
            <Text small muted>
              Will publish as{" "}
              <code>
                {trimmedMarketplaceName || marketplaceSettings.defaultName}
              </code>
              .{" "}
              {publishStatus?.connected
                ? "Saving will regenerate the marketplace and push to GitHub."
                : "Will take effect on your next publish."}
            </Text>
            <Dialog.Footer>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setIsMarketplaceSettingsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !marketplaceNameDirty ||
                  updateMarketplaceSettingsMutation.isPending
                }
              >
                <Button.Text>
                  {updateMarketplaceSettingsMutation.isPending
                    ? publishStatus?.connected
                      ? "Republishing..."
                      : "Saving..."
                    : "Save"}
                </Button.Text>
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Content>
      </Dialog>
    </>
  );
}

// Platform-provided plugin, not a real `Plugin` row — always ships first in
// the marketplace and has no detail page, so it gets its own card rather than
// reusing PluginCard. The accent border + "Platform" badge are its "special
// affordance" distinguishing it from user-created plugins in the grid.
function ObservabilityPluginCard({
  publishStatus,
  enabled,
  canToggle,
  isToggling,
  onEnabledChange,
  isDownloadMenuOpen,
  onDownloadMenuOpenChange,
  isDownloading,
  onDownload,
}: {
  publishStatus: PublishStatusResult | undefined;
  enabled: boolean;
  canToggle: boolean;
  isToggling: boolean;
  onEnabledChange: (enabled: boolean) => void;
  isDownloadMenuOpen: boolean;
  onDownloadMenuOpenChange: (open: boolean) => void;
  isDownloading: boolean;
  onDownload: (platform: ObservabilityDownloadPlatform) => void;
}) {
  const [isInstallSheetOpen, setIsInstallSheetOpen] = useState(false);
  const [isRotateDialogOpen, setIsRotateDialogOpen] = useState(false);
  const { hasScope } = useRBAC();
  // Rotation mints an org-wide ingest credential, so it matches the plugin
  // download's org:admin gate rather than the page's read access.
  const canRotateCredential = hasScope("org:admin");
  const isConnected = !!publishStatus?.connected;
  const installTarget =
    isConnected && publishStatus?.repoOwner && publishStatus.repoName
      ? {
          repoOwner: publishStatus.repoOwner,
          repoName: publishStatus.repoName,
          marketplaceUrl: publishStatus.marketplaceUrl,
        }
      : undefined;

  return (
    <McpEntityCard
      className="border-primary/30 bg-primary/[0.02]"
      icon={<Activity className="text-primary h-10 w-10 opacity-80" />}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Text
            variant="subheading"
            as="div"
            className="text-md truncate"
            title="Observability"
          >
            Observability
          </Text>
          <Badge variant="information">
            <Badge.Text>Platform</Badge.Text>
          </Badge>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={onEnabledChange}
          disabled={!canToggle || isToggling}
          aria-label={
            enabled
              ? "Disable observability plugin"
              : "Enable observability plugin"
          }
        />
      </div>

      <Text small muted className="mb-3 line-clamp-3">
        {enabled
          ? "Forwards tool events from your team's coding agent installs to your project dashboard. Ships first in your marketplace, marked Required."
          : "Disabled for this project. It is not listed in the marketplace and the device agent will not install it."}
      </Text>

      <div className="mt-auto flex items-center justify-between gap-2 pt-2">
        <Text small muted>
          {observabilityInstallHint(enabled, isConnected)}
        </Text>
        {enabled ? (
          // The icon-only MoreActions trigger carries mx-[-4px] to sit flush
          // with a container edge, which here eats the gap against Install and
          // leaves the two hit boxes touching. gap-3 nets the usual 8px.
          <div className="flex items-center gap-3">
            <MoreActions
              triggerAriaLabel="Observability plugin actions"
              triggerDisabled={!canRotateCredential}
              actions={[
                {
                  label: "Rotate credential",
                  icon: "rotate-ccw-key",
                  disabled: !canRotateCredential,
                  description: canRotateCredential
                    ? undefined
                    : "Requires an organization admin",
                  onClick: () => {
                    // Defer until after the menu has fully closed to avoid a
                    // Radix focus-trap/body-lock conflict between the closing
                    // menu and the opening dialog (same pattern as the install
                    // sheet above).
                    setTimeout(() => setIsRotateDialogOpen(true), 0);
                  },
                },
              ]}
            />
            <DropdownMenu
              open={isDownloadMenuOpen}
              onOpenChange={onDownloadMenuOpenChange}
            >
              <DropdownMenuTrigger asChild>
                <PluginInstallButton size="sm" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!installTarget}
                  onClick={() => {
                    // Defer until after the dropdown has fully closed to avoid a
                    // Radix focus-trap/body-lock conflict between the closing
                    // menu and the opening sheet (same pattern as MCPDetails.tsx).
                    setTimeout(() => setIsInstallSheetOpen(true), 0);
                  }}
                >
                  <div className="flex flex-col">
                    <span>GitHub installation (preferred)</span>
                    {!installTarget && (
                      <span className="text-muted-foreground text-xs">
                        Requires marketplace setup
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {OBSERVABILITY_DOWNLOAD_PLATFORMS.map(({ platform, label }) => (
                  <DropdownMenuItem
                    key={platform}
                    disabled={isDownloading}
                    onClick={() => {
                      onDownload(platform);
                    }}
                  >
                    Download as zip — {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>

      {/* Mounted only while open so the rotation mutation is not instantiated
          for every render of this card. */}
      {isRotateDialogOpen && (
        <RotateObservabilityCredentialDialog
          open
          onOpenChange={setIsRotateDialogOpen}
          isDownloading={isDownloading}
          onDownload={onDownload}
        />
      )}

      {/* Reuses the onboarding wizard's platform-by-platform setup sheet
          (real per-platform slugs, API key minting, full instructions)
          instead of the generic single-plugin install dialog — no
          preselected platform, so the sheet opens on its own platform
          picker first. */}
      <PlatformInstrumentationSheet
        open={isInstallSheetOpen}
        onOpenChange={setIsInstallSheetOpen}
      />
    </McpEntityCard>
  );
}

function observabilityInstallHint(
  enabled: boolean,
  isConnected: boolean,
): string {
  if (!enabled) {
    return "Not included in your marketplace";
  }
  if (isConnected) {
    return "Included in your marketplace";
  }
  return "Available as a direct download";
}

function PlatformMCPPluginCard(): JSX.Element | null {
  const [installOpen, setInstallOpen] = useState(false);
  const { hasScope } = useRBAC();
  if (!hasScope("org:admin")) return null;

  return (
    <McpEntityCard
      className="border-primary/30 bg-primary/[0.02]"
      icon={<Network className="text-primary h-10 w-10 opacity-80" />}
    >
      <div className="mb-2 flex items-center gap-1.5">
        <Text
          variant="subheading"
          as="div"
          className="text-md truncate"
          title="Platform MCP"
        >
          Platform MCP
        </Text>
        <Badge variant="information">
          <Badge.Text>Platform</Badge.Text>
        </Badge>
      </div>

      <Text small muted className="mb-3 line-clamp-3">
        Manage MCPs, Risk Policies and explore logs in your favorite agent.
      </Text>

      <div className="mt-auto flex items-center justify-between gap-2 pt-2">
        <Text small muted>
          Available from the public Speakeasy marketplace
        </Text>
        <PluginInstallButton size="sm" onClick={() => setInstallOpen(true)} />
      </div>

      <PlatformMCPOnboardingContent
        sheetOnly
        setupOpen={installOpen}
        onSetupOpenChange={setInstallOpen}
      />
    </McpEntityCard>
  );
}

function PluginGrid({
  plugins,
  publishStatus,
  searchQuery,
}: {
  plugins: Plugin[];
  publishStatus: PublishStatusResult | undefined;
  searchQuery: string;
}) {
  if (plugins.length === 0) {
    return (
      <div className="space-y-4">
        {searchQuery ? (
          <Text muted>No plugins matching &ldquo;{searchQuery}&rdquo;</Text>
        ) : null}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      {plugins.map((plugin) => (
        <PluginCard
          key={plugin.id}
          plugin={plugin}
          publishStatus={publishStatus}
        />
      ))}
    </div>
  );
}
